import React, { Fragment, useState, useEffect } from "react";
import { IconCalendar, IconChevronDown } from "@tabler/icons-react";
import { CommonUtils, StorageUtils, DatetimeUtils } from "utils";
import "chart.js/auto";
import { Doughnut } from "react-chartjs-2";
import { useStatisticUserGet } from "hooks";
import { KeyConstant, LangConstant, SystemConstant } from "const";
import { useTranslation } from "react-i18next";
import SelectDateTime from "./SelectDateTime";
import UserListDialog from "./UserListDialog";

// Màu sắc 4 trạng thái "Chức năng hoạt động" — nhất quán với ACTIVE_FUNCTION_DISPLAY.
// Thứ tự: Vô hiệu hóa, Đã đăng nhập, Chưa đăng nhập, Ngưng hoạt động.
const ACTIVE_FUNCTION_COLORS = ["#d63939", "#2fb344", "#f76707", "#868e96"];

// Ánh xạ index slice → { branchStatus, filterState } để truyền vào UserListDialog.
// index 0: Vô hiệu hóa  → branch_status=0 (bất kỳ state)
// index 1: Đã đăng nhập → branch_status=1, state=1
// index 2: Chưa đăng nhập → branch_status=1, state=3
// index 3: Ngưng hoạt động → branch_status=1, state=5
const SLICE_FILTER = [
  { branchStatus: SystemConstant.BRANCH_STATUS.DISABLED, filterState: undefined },
  { branchStatus: SystemConstant.BRANCH_STATUS.ACTIVE, filterState: SystemConstant.STATE.ACTIVE },
  { branchStatus: SystemConstant.BRANCH_STATUS.ACTIVE, filterState: SystemConstant.STATE.PENDING },
  { branchStatus: SystemConstant.BRANCH_STATUS.ACTIVE, filterState: SystemConstant.STATE.NO_ACTIVE_DEVICES },
];

const UserStatistic = ({ unitList }) => {
  const { t: getLabel } = useTranslation();
  const { getStatisticUser } = useStatisticUserGet();
  const branchName = StorageUtils.getStoreData(KeyConstant.KEY_BRANCH_NAME);

  const [unitId, setUnitId] = useState("0");
  const [labelCalendar, setLabelCalendar] = useState(getLabel(LangConstant.TXT_DATE_TODAY));
  const [labelChart, setLabelChart] = useState(branchName);
  const [dataChartUser, setDataChartUser] = useState("");
  const [dataChartActive, setDataChartActive] = useState("");
  const [optionsChartUser, setOptionsChartUser] = useState("");
  const [optionsChartActive, setOptionsChartActive] = useState("");
  const [totalUser, setTotalUser] = useState(0);
  const [totalActive, setTotalActive] = useState(0);
  const [totalElement, setTotalElement] = useState(0);
  const [sinceTime, setSinceTime] = useState(CommonUtils.getStartToday());
  const [lastTime, setLastTime] = useState(null);
  const [openDateTime, setOpenDateTime] = useState(false);

  const [openTableOfUnit, setOpenTableOfUnit] = useState(false);
  const [openTableOfDepartment, setOpenTableOfDepartment] = useState(false);
  // Dialog cho từng trạng thái "Chức năng hoạt động"
  const [activeFnDialog, setActiveFnDialog] = useState(null); // { branchStatus, filterState, title }
  const [choosedElementId, setChoosedElementId] = useState("");
  const [titleDataTable, setTitleDataTable] = useState("");

  const [namesLabelUser, setNamesLabelUser] = useState([]);
  const [colorsLabelUser, setColorsLabelUser] = useState([]);

  const onChangeUnit = e => {
    setUnitId(String(e.target.value));
  };

  const closeDialogDataTable = () => {
    setChoosedElementId(null);
    setOpenTableOfUnit(false);
    setOpenTableOfDepartment(false);
    setActiveFnDialog(null);
    setTitleDataTable("");
  };

  useEffect(() => {
    const fetchData = async () => {
      const payload = {};
      if (String(unitId) !== "0") {
        payload.unit_id = unitId;
      }
      if (sinceTime) {
        payload.since_time = sinceTime;
      }
      if (lastTime) {
        payload.last_time = lastTime;
      }
      const response = await getStatisticUser(payload);
      if (response) {
        const label = response.data.data.map(item => item.name);
        const data = response.data.data.map(item => item.members);
        if (String(unitId) === "0") {
          setLabelChart(branchName);
        } else {
          const foundUnit = unitList.find(item => String(item.unitId) === String(unitId));
          setLabelChart(foundUnit ? foundUnit.unitName : branchName);
        }

        setTotalUser(data.reduce((sum, element) => sum + element, 0));
        setTotalElement(response.data.data.filter(item => item.id !== null).length);

        // Tổng 4 trạng thái "Chức năng hoạt động"
        const cntDisabled = response.data.count_disabled || 0;
        const cntActive = response.data.count_active || 0;
        const cntPending = response.data.count_pending || 0;
        const cntSuspended = response.data.count_no_active_devices || 0;
        setTotalActive(cntDisabled + cntActive + cntPending + cntSuspended);

        let colorsUser = label.map(() => CommonUtils.getRandomColor());
        setNamesLabelUser(label);
        setColorsLabelUser(colorsUser);

        const dataSetUser = {
          labels: label,
          datasets: [
            {
              label: getLabel(LangConstant.TXT_STATISTIC_TOTAL_USERS),
              data: data,
              backgroundColor: colorsUser,
              hoverOffset: 4,
            },
          ],
        };
        setDataChartUser(dataSetUser);

        // 4 slice — thứ tự khớp SLICE_FILTER
        const dataSetActive = {
          labels: [
            getLabel(LangConstant.TXT_STATISTIC_FN_DISABLED),
            getLabel(LangConstant.TXT_STATISTIC_FN_LOGGED_IN),
            getLabel(LangConstant.TXT_STATISTIC_FN_NOT_LOGGED_IN),
            getLabel(LangConstant.TXT_STATISTIC_FN_SUSPENDED),
          ],
          datasets: [
            {
              label: getLabel(LangConstant.TXT_STATISTIC_TOTAL_USERS),
              data: [cntDisabled, cntActive, cntPending, cntSuspended],
              backgroundColor: ACTIVE_FUNCTION_COLORS,
              hoverOffset: 4,
            },
          ],
        };
        setDataChartActive(dataSetActive);

        const sliceTitles = [
          getLabel(LangConstant.TXT_STATISTIC_FN_DISABLED),
          getLabel(LangConstant.TXT_STATISTIC_FN_LOGGED_IN),
          getLabel(LangConstant.TXT_STATISTIC_FN_NOT_LOGGED_IN),
          getLabel(LangConstant.TXT_STATISTIC_FN_SUSPENDED),
        ];

        const optionsDataUser = {
          cutout: "70%",
          radius: "80%",
          responsive: true,
          plugins: {
            legend: {
              position: "bottom",
              maxWidth: 220,
              display: false,
              labels: {
                usePointStyle: false,
                boxWidth: 15,
                boxHeight: 15,
              },
            },
          },
          onClick: (e, activeEls) => {
            const dataIndex = activeEls[0]?.index;
            // Click vào vùng trống của biểu đồ → không mở dialog
            if (dataIndex === undefined || dataIndex === null) return;
            const id = response.data.data[dataIndex]?.id;
            const name = response.data.data[dataIndex]?.name;

            setChoosedElementId(id);
            setTitleDataTable(name);
            // unitId là chuỗi ("0" = toàn chi nhánh) → so sánh theo chuỗi
            if (String(unitId) === "0") {
              setOpenTableOfUnit(true);
            } else {
              setOpenTableOfDepartment(true);
            }
          },
        };
        setOptionsChartUser(optionsDataUser);

        const optionsDataActive = {
          cutout: "70%",
          radius: "80%",
          responsive: true,
          plugins: {
            legend: {
              position: "bottom",
              maxWidth: 220,
              labels: {
                usePointStyle: false,
                boxWidth: 15,
                boxHeight: 15,
              },
            },
          },
          onClick: (e, activeEls) => {
            const dataIndex = activeEls[0]?.index;
            if (dataIndex === undefined || dataIndex === null) return;
            const filter = SLICE_FILTER[dataIndex];
            if (!filter) return;
            setActiveFnDialog({
              branchStatus: filter.branchStatus,
              filterState: filter.filterState,
              title: sliceTitles[dataIndex],
            });
          },
        };
        setOptionsChartActive(optionsDataActive);
      }
    };

    fetchData();
  }, [unitId, sinceTime, lastTime]);

  return (
    <Fragment>
      <div className="d-flex justify-content-between align-items-center pb-2 mb-2 border-bottom">
        <h3 className="m-0">{getLabel(LangConstant.TXT_STATISTIC_USER_OVERVIEW)}</h3>
        <div className="d-flex gap-3 position-relative">
          <select
            className="form-select"
            style={{ width: "200px" }}
            onChange={onChangeUnit}
            value={unitId}
          >
            {unitList.map(data => (
              <option key={data.unitId} value={data.unitId}>
                {data.unitName}
              </option>
            ))}
          </select>

          <div
            className="btn btn-outline-secondary d-flex align-items-center gap-2"
            style={{ minWidth: "200px", maxWidth: "250px" }}
            onClick={() => setOpenDateTime(!openDateTime)}
          >
            <IconCalendar size={18} />
            <span className="flex-grow-1">{labelCalendar}</span>
            <IconChevronDown size={16} />
          </div>

          <div style={{ position: "absolute", zIndex: 4, top: "42px", right: "0px" }}>
            <SelectDateTime
              open={openDateTime}
              setLabel={setLabelCalendar}
              onClose={() => setOpenDateTime(false)}
              setSinceTime={setSinceTime}
              setLastTime={setLastTime}
            />
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-around">
        <div className="card" style={{ width: "45%", marginBottom: "12px" }}>
          <div className="card-body">
            <h4 className="card-title">{getLabel(LangConstant.TXT_STATISTIC_USER_COUNT_IN, { name: labelChart })}</h4>
            <div className="d-flex flex-column align-items-center w-100 position-relative">
              <div style={{ width: "100%", maxWidth: "350px" }}>
                {Boolean(dataChartUser) && <Doughnut data={dataChartUser} options={optionsChartUser} />}
              </div>
              <div className="d-flex flex-column align-items-center position-absolute" style={{ top: "50%", left: "50%", transform: "translate(-50%, -50%)", pointerEvents: "none" }}>
                <span className="text-secondary" style={{ fontSize: "11px" }}>{getLabel(LangConstant.TXT_STATISTIC_TOTAL_USERS)}</span>
                <strong>{totalUser}</strong>
                <span className="text-secondary" style={{ fontSize: "11px" }}>
                  {String(unitId) === "0" ? getLabel(LangConstant.TXT_STATISTIC_TOTAL_UNITS) : getLabel(LangConstant.TXT_STATISTIC_TOTAL_DEPARTMENTS)}
                </span>
                <strong>{totalElement}</strong>
              </div>
              <div className="d-flex flex-wrap gap-1 px-3">
                {namesLabelUser.map((name, index) => (
                  <div className="d-flex align-items-center" key={index}>
                    <span
                      style={{
                        backgroundColor: colorsLabelUser[index],
                        width: "14px",
                        height: "14px",
                        display: "inline-block",
                      }}
                    ></span>
                    <span className="ms-1 me-2" style={{ fontSize: "12px" }}>{name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="card" style={{ width: "45%", marginBottom: "12px" }}>
          <div className="card-body">
            <h4 className="card-title">{getLabel(LangConstant.TXT_STATISTIC_USER_STATUS)}</h4>
            <div className="d-flex flex-column align-items-center w-100 position-relative">
              <div style={{ width: "100%", maxWidth: "350px" }}>
                {Boolean(dataChartActive) && <Doughnut data={dataChartActive} options={optionsChartActive} />}
              </div>
              <div className="d-flex flex-column align-items-center position-absolute" style={{ top: "50%", left: "50%", transform: "translate(-50%, -50%)", pointerEvents: "none" }}>
                <span className="text-secondary" style={{ fontSize: "11px" }}>{getLabel(LangConstant.TXT_STATISTIC_TOTAL_USERS)}</span>
                <strong>{totalActive}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p className="text-secondary small">
        {getLabel(LangConstant.TXT_STATISTIC_DATA_UPDATED_AT, { time: DatetimeUtils.convertMillisecondsToDateTime(Date.now()) })}
      </p>

      {openTableOfUnit && (
        <UserListDialog
          open={openTableOfUnit}
          unitId={choosedElementId}
          title={titleDataTable}
          onClose={closeDialogDataTable}
          sinceTime={sinceTime}
          lastTime={lastTime}
          joinStatus={1}
          includeUnit={Boolean(choosedElementId) ? true : false}
          includeDepartment={true}
        />
      )}

      {openTableOfDepartment && (
        <UserListDialog
          open={openTableOfDepartment}
          unitId={unitId}
          departmentId={choosedElementId}
          title={titleDataTable}
          onClose={closeDialogDataTable}
          sinceTime={sinceTime}
          lastTime={lastTime}
          joinStatus={1}
          includeUnit={String(unitId) !== "-1"}
          includeDepartment={Boolean(choosedElementId) ? true : false}
        />
      )}

      {/* Dialog trạng thái "Chức năng hoạt động" — click vào một slice trong biểu đồ.
          Backend coi is_include_unit/department=false là LOẠI TRỪ người có đơn vị/phòng ban,
          nên luôn gửi true; việc lọc theo đơn vị đã do unit_id đảm nhiệm. */}
      {activeFnDialog && (
        <UserListDialog
          open={Boolean(activeFnDialog)}
          unitId={unitId}
          title={activeFnDialog.title}
          onClose={closeDialogDataTable}
          sinceTime={sinceTime}
          lastTime={lastTime}
          branchStatus={activeFnDialog.branchStatus}
          filterState={activeFnDialog.filterState}
          includeUnit={true}
          includeDepartment={true}
        />
      )}
    </Fragment>
  );
};

export default UserStatistic;
